import { createMockContact } from '@indutrust/shared-model';
import type { FactoryEdit, FactoryEditPayload } from '../factory-edit';
import { MOCK_AAE_FACTORY_ID, MOCK_FACTORY_AAE } from './aae-factory.mock';

/**
 * Demo map pin for the canonical factory (Premier Composite Technologies at
 * Dubai Investment Park — sample factory id 1).
 */
export const MOCK_FACTORY_DEMO_LATITUDE = 24.9786301;
export const MOCK_FACTORY_DEMO_LONGITUDE = 55.1685973;

const MOCK_FACTORY_DEMO_DESCRIPTION =
  'Advanced composite design and manufacturing for aerospace, marine and ' +
  'industrial structures across the United Arab Emirates. Specialises in ' +
  'carbon-fibre and glass-fibre laminates, tooling, and large-scale ' +
  'composite assembly with on-site commissioning support.';

/**
 * Canonical mock {@link FactoryEdit} used by tests, MSW handlers and the
 * demo build. Location uses the real sample plant name and coordinates;
 * profile, contact, and capability fields remain invented demo data.
 * Override via {@link createMockFactoryEdit} when a sparse fixture is needed.
 */
export const MOCK_FACTORY_EDIT: FactoryEdit = {
  id: 1,
  slug: 'premier-composite-technologies',
  name: 'Premier Composite Technologies (PCT HQ)',
  status: 'draft',
  verified: false,
  created_at: '2025-01-01T00:00:00Z',
  updated_at: '2025-01-02T00:00:00Z',
  country_id: 1,
  industrial_area_id: 2,
  industrial_area_name: 'Dubai Investment Park',
  industrial_subtypes: [],
  industrial_subtype_ids: [],
  capabilities: [],
  capability_ids_array: [],
  capability_ids: [],
  address: 'Dubai Investment Park, Dubai, United Arab Emirates',
  po_box: 'PO Box 12345, Dubai, UAE',
  latitude: MOCK_FACTORY_DEMO_LATITUDE,
  longitude: MOCK_FACTORY_DEMO_LONGITUDE,
  description: MOCK_FACTORY_DEMO_DESCRIPTION,
  creation_date: '2020-01-01',
  website: 'https://www.pct.ae',
  linked_in_url:
    'https://www.linkedin.com/company/premier-composite-technologies',
  project_references: [],
  project_ref_on_demand: false,
  turnover: 12_500_000,
  turnover_on_demand: false,
  company_size: 85,
  headcount_production: 60,
  headcount_management: 12,
  workshop_used: 4500,
  workshop_available: 1200,
  trade_license_number: 100234567890,
  certification: ['ISO 9001:2015', 'ISO 14001:2015', 'ISO 45001:2018'],
  logo: null,
  images: [],
  current_capabilities: [],
  shareholder_uae: '100% UAE-owned',
  product_reference: 'Composite structures and tooling',
  activity_reference: 'Composite manufacturing, tooling, assembly',
  contacts: [
    createMockContact({
      id: 1,
      type: 'sales',
      first_name: 'Fatima',
      last_name: 'Al Nuaimi',
      email: 'sales@pct.demo',
      number: '+971 4 555 0100',
      has_whatsapp: true,
      country_id: 1,
      country_code: 'AE',
      indutrustMainPoc: true,
    }),
    createMockContact({
      id: 2,
      type: 'admin',
      first_name: 'Omar',
      last_name: 'Hassan',
      email: 'operations@pct.demo',
      number: '+971 4 555 0101',
      has_whatsapp: false,
      country_id: 1,
      country_code: 'AE',
      indutrustMainPoc: false,
    }),
  ],
  prospects: [],
  can_edit: true,
};

/**
 * Published + verified variant of {@link MOCK_FACTORY_EDIT}.
 *
 * Used as the seed for the demo build and the mocked e2e suite so all sections
 * of the enhanced {@link import('@indutrust/factory-feature').FactoryView} are
 * visible at `localhost:4300/factory/1`. Includes capabilities, industrial
 * subtypes, and a sample image array to exercise the gallery and capabilities
 * sections.
 */
export const MOCK_FACTORY_PUBLISHED: FactoryEdit = {
  ...MOCK_FACTORY_EDIT,
  id: 1,
  status: 'published',
  verified: true,
  industrial_subtypes: [
    { id: 1, name: 'CNC Machining', slug: 'cnc-machining' },
    { id: 2, name: 'Metal Fabrication', slug: 'metal-fabrication' },
  ],
  industrial_subtype_ids: [1, 2],
  capabilities: [
    { id: 1, name: 'CNC Machining', slug: 'cnc-machining' },
    { id: 2, name: 'Sheet Metal Fabrication', slug: 'sheet-metal-fabrication' },
  ],
  capability_ids_array: [1, 2],
  capability_ids: [11, 12],
  logo: 'https://placehold.co/200x200?text=PCT',
  images: [
    'https://placehold.co/1200x800?text=Factory+Photo+1',
    'https://placehold.co/1200x800?text=Factory+Photo+2',
    'https://placehold.co/1200x800?text=Factory+Photo+3',
  ],
};

/**
 * Builds a {@link FactoryEdit} from {@link MOCK_FACTORY_EDIT} with
 * optional overrides.
 */
export function createMockFactoryEdit(
  overrides?: Partial<FactoryEdit>
): FactoryEdit {
  return { ...MOCK_FACTORY_EDIT, ...overrides };
}

/** Minimal map-pin fields used to seed discovery factory profiles. */
export type MapPinFactorySeed = Pick<
  FactoryEdit,
  'id' | 'name' | 'industrial_area_id' | 'latitude' | 'longitude' | 'verified'
>;

/**
 * Builds a published {@link FactoryEdit} from a map-discovery pin so
 * `GET /factories/:id` stays aligned with `GET /filters_v2` in the demo build.
 */
export function createMockPublishedFactoryEditFromMapPin(
  pin: MapPinFactorySeed,
  options: { industrialAreaName?: string | null } = {}
): FactoryEdit {
  if (pin.id === MOCK_AAE_FACTORY_ID) {
    return {
      ...MOCK_FACTORY_AAE,
      verified: pin.verified,
      latitude: pin.latitude,
      longitude: pin.longitude,
      industrial_area_id: pin.industrial_area_id,
      industrial_area_name:
        options.industrialAreaName ?? MOCK_FACTORY_AAE.industrial_area_name,
    };
  }

  const slug = pin.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  return createMockFactoryEdit({
    id: pin.id,
    name: pin.name,
    slug,
    status: 'published',
    verified: pin.verified,
    latitude: pin.latitude,
    longitude: pin.longitude,
    industrial_area_id: pin.industrial_area_id,
    industrial_area_name: options.industrialAreaName ?? null,
  });
}

/**
 * Builds a list of mock {@link FactoryEdit} entries. Each entry gets a
 * unique `id`, `name` and `slug` so collection-store specs see distinct
 * entities.
 */
export function createMockFactoryEditList(
  count = 3,
  overrideAt?: (index: number) => Partial<FactoryEdit>
): FactoryEdit[] {
  return Array.from({ length: count }, (_, i) =>
    createMockFactoryEdit({
      id: i + 1,
      name: `Premier Composite Technologies ${i + 1}`,
      slug: `premier-composite-technologies-${i + 1}`,
      ...overrideAt?.(i),
    })
  );
}

/**
 * Canonical mock {@link FactoryEditPayload} (the subset accepted by
 * `POST/PATCH /factories`).
 */
export const MOCK_FACTORY_EDIT_PAYLOAD: FactoryEditPayload = {
  name: 'Premier Composite Technologies (PCT HQ)',
  industrial_area_id: MOCK_FACTORY_EDIT.industrial_area_id,
  longitude: MOCK_FACTORY_EDIT.longitude,
  latitude: MOCK_FACTORY_EDIT.latitude,
  address: MOCK_FACTORY_EDIT.address,
  description: MOCK_FACTORY_EDIT.description,
  creation_date: MOCK_FACTORY_EDIT.creation_date,
  trade_license_number: MOCK_FACTORY_EDIT.trade_license_number,
  website: MOCK_FACTORY_EDIT.website,
  certification: MOCK_FACTORY_EDIT.certification,
  shareholder_uae: MOCK_FACTORY_EDIT.shareholder_uae,
  turnover: MOCK_FACTORY_EDIT.turnover,
  product_reference: MOCK_FACTORY_EDIT.product_reference,
  activity_reference: MOCK_FACTORY_EDIT.activity_reference,
  industrial_subtype_ids: [],
  capability_ids: [],
  country_id: MOCK_FACTORY_EDIT.country_id,
  workshop_used: MOCK_FACTORY_EDIT.workshop_used,
  workshop_available: MOCK_FACTORY_EDIT.workshop_available,
  headcount_production: MOCK_FACTORY_EDIT.headcount_production,
  headcount_management: MOCK_FACTORY_EDIT.headcount_management,
  project_ref_on_demand: false,
  turnover_on_demand: false,
};

/** Builds a {@link FactoryEditPayload} from the canonical default. */
export function createMockFactoryEditPayload(
  overrides?: Partial<FactoryEditPayload>
): FactoryEditPayload {
  return { ...MOCK_FACTORY_EDIT_PAYLOAD, ...overrides };
}
